import { useEffect, useState, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { useWebDriverLocation } from "@/hooks/useWebDriverLocation";
import { useWakeLock } from "@/hooks/useWakeLock";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsActualMobileDevice } from "@/hooks/useIsActualMobileDevice";
import { onTrackingEvent, type TrackingEvent } from "@/lib/trackingBridge";
import {
  checkLocationPermission,
  invalidatePermissionCache,
  primeLocationPermissionOnce,
  startNativeLocationWatch,
  stopNativeLocationWatch,
  onAppResume,
} from "@/lib/nativeLocation";
import { AdaptiveTrackingFilter } from "@/lib/adaptiveTrackingFilter";
import {
  GpsWritePolicy,
  LowPowerRecovery,
  RECOVERY_POSITION_OPTIONS,
  StaleWatcherGuard,
  STALE_WATCHER_MS,
  isValidFix,
  type GpsWriteReason,
} from "@/lib/gpsWritePolicy";
import { createDriverLocationWriter, type DriverLocationWriter } from "@/lib/driverLocationWriter";
import { App as CapacitorApp } from "@capacitor/app";
import {
  emitTrackingDiag,
  categoriseTrackingError,
  LocationCallbackSampler,
} from "@/lib/trackingDiagnostics";

/**
 * DriverLocationTracker - Silent background component for GPS tracking
 * 
 * On native (Capacitor): uses Capacitor Geolocation.watchPosition for true
 * background + locked-screen tracking (with iOS backgroundModes: ['location']).
 * 
 * On mobile web: uses navigator.geolocation (web API) with Wake Lock.
 * 
 * Tracking activates when driver has active jobs requiring tracking.
 * Tracking stops when all jobs are complete or cancelled.
 */
/**
 * Job-state-aware tracking states.
 * - IDLE: no active jobs → no watcher at all (battery = 0)
 * - ASSIGNED: driver has a job but POC not yet completed → low-power tracking
 * - EN_ROUTE: POC done, POD pending → adaptive (high accuracy when moving fast)
 * (ARRIVED is intentionally not modelled — POD completion stops tracking instead.)
 */
import {
  resolveResumeAction,
  shouldRunContinuousTracking,
  type TrackingState,
} from "@/lib/trackingPolicy";

/** F-08A: slow assignment re-check while tracking (reassignment away is not pushed). */
export const ASSIGNMENT_RECHECK_MS = 2 * 60 * 1000;

/** Freshly derived tracking inputs returned by `checkDriverAndJobs`. */
interface TrackingSnapshot {
  state: TrackingState;
  isDriverRecord: boolean;
  hasSession: boolean;
}

export const DriverLocationTracker = () => {
  const isMobileDevice = useIsActualMobileDevice();
  const isNative = Capacitor.isNativePlatform();
  const [isDriverRecord, setIsDriverRecord] = useState<boolean | null>(null);
  const [trackingState, setTrackingState] = useState<TrackingState>("IDLE");
  // Mirror of `isMobileDevice` readable inside long-lived resume callbacks.
  const isMobileDeviceRef = useRef(isMobileDevice);
  isMobileDeviceRef.current = isMobileDevice;
  const hasStartedRef = useRef(false);
  const nativeCleanupRef = useRef<(() => void) | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const lastNativePositionRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastGpsCallbackRef = useRef<number>(0); // timestamp of last GPS callback
  // STALE_WATCHER_MS (2 min without a GPS callback = stale) comes from gpsWritePolicy.

  // Adaptive filter — runs in JS on top of a single native watcher. Tightens
  // or loosens the *effective* distance filter based on speed/movement, with
  // ZERO watcher restarts. See src/lib/adaptiveTrackingFilter.ts.
  const adaptiveFilterRef = useRef<AdaptiveTrackingFilter | null>(null);

  // STABILITY: a single watcher runs for the lifetime of an active job.
  // We do NOT restart on ASSIGNED↔EN_ROUTE transitions or speed changes —
  // every restart is a battery spike on iOS/Android. The native filter is
  // chosen once when the watcher starts; runtime tuning happens entirely in
  // JS via AdaptiveTrackingFilter.
  // Mirror of `trackingState` accessible inside long-lived callbacks
  const trackingStateRef = useRef<TrackingState>("IDLE");

  // Flapping guard: enforce a minimum gap between ASSIGNED↔EN_ROUTE swaps.
  // IDLE transitions (start/stop watcher) bypass this and apply immediately.
  const lastStateChangeRef = useRef<number>(0);
  const STATE_FLAP_GUARD_MS = 60_000;

  // 24-hour cooldown for tracking toast
  const TOAST_COOLDOWN_KEY = 'locationTrackingToastLastShown';
  const hasShownStartToastRef = useRef((() => {
    const lastShown = localStorage.getItem(TOAST_COOLDOWN_KEY);
    if (!lastShown) return false;
    return Date.now() - Number(lastShown) < 24 * 60 * 60 * 1000;
  })());
  const wasTrackingRef = useRef(false);
  const { toast } = useToast();
  const { requestWakeLock, releaseWakeLock, isSupported: isWakeLockSupported } = useWakeLock();

  // CONTINUOUS TRACKING SCOPE (iOS location-usage fix):
  // A continuous GPS watcher runs ONLY during an active collection→delivery run
  // (EN_ROUTE: POC done, POD pending). Merely having a job assigned (ASSIGNED)
  // no longer starts a watcher — that kept CLLocationManager alive all day and
  // made iOS report TransportMate as constantly using location.
  const shouldTrack = shouldRunContinuousTracking({
    isMobileDevice,
    isDriverRecord,
    trackingState,
  });

  // Web-based tracking (only used on mobile web, not native)
  // LOW power by default — switching to high accuracy is handled by the watcher when driving
  const {
    isTracking: isWebTracking,
    startTracking: startWebTracking,
    stopTracking: stopWebTracking,
  } = useWebDriverLocation(false, {
    enableHighAccuracy: false,
    autoManage: false,
  });

  // Shared write policy (src/lib/gpsWritePolicy.ts):
  //   - FIRST fix of a session writes immediately (short jobs aren't stale)
  //   - a jump > 300m since the last write bypasses the 60s limit
  //   - otherwise at most one write per 60s
  // Writes go through a bounded, age-limited retry queue (driverLocationWriter).
  const writePolicyRef = useRef(new GpsWritePolicy());
  const writerRef = useRef<DriverLocationWriter | null>(null);
  if (!writerRef.current) writerRef.current = createDriverLocationWriter(isNative ? "native" : "web");
  // Diagnostics only: samples watcher callbacks (first + one per 5 min).
  const callbackSamplerRef = useRef(new LocationCallbackSampler());
  const noteCallback = () => {
    const meta = callbackSamplerRef.current.onCallback();
    if (meta) emitTrackingDiag("location_callback", { ...meta, state: trackingStateRef.current });
  };
  const staleGuardRef = useRef(new StaleWatcherGuard());

  // Track consecutive DB failures to surface persistent issues
  const consecutiveFailuresRef = useRef<number>(0);
  const FAILURE_TOAST_THRESHOLD = 3;
  const hasShownFailureToastRef = useRef(false);

  const commitWrite = async (lat: number, lng: number, reason: GpsWriteReason) => {
    writePolicyRef.current.record({ lat, lng }, Date.now());
    const ok = await writerRef.current!.write(lat, lng);
    if (ok) {
      if (consecutiveFailuresRef.current > 0) {
        console.log(`[DriverLocationTracker] DB write recovered after ${consecutiveFailuresRef.current} failures`);
        consecutiveFailuresRef.current = 0;
        hasShownFailureToastRef.current = false;
      }
      return;
    }
    consecutiveFailuresRef.current++;
    if (consecutiveFailuresRef.current >= FAILURE_TOAST_THRESHOLD && !hasShownFailureToastRef.current) {
      hasShownFailureToastRef.current = true;
      toast({
        title: "Location updates failing",
        description: "Your location may not be visible to your team. Check your connection.",
        variant: "destructive",
      });
    }
  };

  // Shared location update function for native tracking
  const updateLocationInDatabase = async (lat: number, lng: number, forceWrite = false) => {
    const reason: GpsWriteReason | null = forceWrite
      ? (isValidFix({ lat, lng }) ? "seed" : null)
      : writePolicyRef.current.evaluate({ lat, lng }, Date.now());
    if (!reason) return;
    await commitWrite(lat, lng, reason);
  };

  // Low-power recovery (NOT a heartbeat): at most ONE low-accuracy request per
  // silent period, checked from the existing watcher-health interval.
  const restartWatcherRef = useRef<((trigger: string) => Promise<void>) | null>(null);
  const recoveryRef = useRef<LowPowerRecovery | null>(null);
  if (!recoveryRef.current) {
    recoveryRef.current = new LowPowerRecovery({
      now: () => Date.now(),
      policy: writePolicyRef.current,
      isActive: () => hasStartedRef.current && trackingStateRef.current === "EN_ROUTE",
      requestPosition: async () => {
        try {
          const pos = await Promise.race([
            Geolocation.getCurrentPosition(RECOVERY_POSITION_OPTIONS),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error("RECOVERY_TIMEOUT")), 11_000)),
          ]);
          return { lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch {
          return null;
        }
      },
      onStationary: (fix) => { void commitWrite(fix.lat, fix.lng, "stationary_confirm"); },
      restartWatcher: async () => { await restartWatcherRef.current?.("recovery_stalled"); },
      diag: (event, fields) => emitTrackingDiag(event, { ...fields, state: trackingStateRef.current }),
    });
  }

  // Stable ref to the check function so bridge listener can call it
  const checkDriverAndJobsRef = useRef<(() => void) | null>(null);
  const driverInfoRef = useRef<{ id: string; companyId: string | null } | null>(null);

  // Stable ref to the immediate-seed function. Used both right after the watcher
  // starts (so the BG plugin's slow first callback doesn't leave the map stale)
  // and on ASSIGNED→EN_ROUTE transitions (so POC instantly refreshes the marker).
  const seedImmediatePositionRef = useRef<(() => Promise<void>) | null>(null);
  const lastSeedAtRef = useRef<number>(0);
  const SEED_THROTTLE_MS = 15_000; // never re-seed more than once every 15s

  seedImmediatePositionRef.current = async () => {
    if (!Capacitor.isNativePlatform()) return;
    const now = Date.now();
    if (now - lastSeedAtRef.current < SEED_THROTTLE_MS) {
      emitTrackingDiag("seed_throttled", { state: trackingStateRef.current });
      return;
    }
    lastSeedAtRef.current = now;
    emitTrackingDiag("seed_requested", { state: trackingStateRef.current });

    try {
      // Foreground Geolocation API typically returns within 2-5s on iOS, vs.
      // 15-60s for the BG plugin's first callback after addWatcher.
      const pos = await Promise.race([
        Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 8_000,
          maximumAge: 30_000,
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("SEED_TIMEOUT")), 9_000),
        ),
      ]);
      console.log("[DriverLocationTracker] Seed fix acquired");
      emitTrackingDiag("seed_succeeded", { state: trackingStateRef.current });
      // Force-write so we bypass throttle even if the watcher already wrote recently.
      await updateLocationInDatabase(pos.coords.latitude, pos.coords.longitude, true);
      lastNativePositionRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      lastGpsCallbackRef.current = Date.now();
    } catch (err: any) {
      console.warn("[DriverLocationTracker] Seed fix failed:", err?.message || err);
      emitTrackingDiag("seed_failed", {
        state: trackingStateRef.current,
        error_category: categoriseTrackingError(err),
      });
    }
  };

  // Check if user has a driver record and determine current tracking state.
  // Returns the freshly derived snapshot so callers (app resume) can evaluate
  // the CURRENT policy instead of acting on stale state.
  const checkDriverAndJobs = useCallback(async (): Promise<TrackingSnapshot> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      emitTrackingDiag("state_evaluated", { has_session: false, driver_found: false, next_state: "IDLE", reason: "no_session" });
      setIsDriverRecord(false);
      setTrackingState("IDLE");
      return { state: "IDLE", isDriverRecord: false, hasSession: false };
    }
    const user = session.user;

    const { data: driver } = await supabase
      .from("drivers")
      .select("id, company_id")
      .eq("user_id", user.id)
      .maybeSingle();

    setIsDriverRecord(!!driver);
    driverInfoRef.current = driver ? { id: driver.id, companyId: driver.company_id } : null;

    if (!driver) {
      emitTrackingDiag("state_evaluated", { has_session: true, driver_found: false, next_state: "IDLE", reason: "no_driver_record" });
      setTrackingState("IDLE");
      return { state: "IDLE", isDriverRecord: false, hasSession: true };
    }

    // Pull active assigned/in-flight jobs (POD not done, not cancelled)
    // ASSIGNED → driver has the job but POC not yet completed
    // EN_ROUTE → POC completed (or skipped) within 48h
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
    const { data: activeJobs } = await supabase
      .from("jobs")
      .select("id, status, collection_completed_at, collection_skipped")
      .eq("driver_id", driver.id)
      .is("delivery_completed_at", null)
      .in("status", ["pending", "in_progress", "awaiting_delivery"])
      .is("cancelled_at", null);

    const jobs = activeJobs || [];

    // EN_ROUTE: any job with POC done (or skipped) within the 48h window
    const enRouteJobs = jobs.filter((j) => {
      const pocDoneOrSkipped = !!j.collection_completed_at || !!j.collection_skipped;
      const recent = !j.collection_completed_at || j.collection_completed_at >= cutoff;
      return pocDoneOrSkipped && recent;
    });

    // ASSIGNED: any active job that is NOT yet en-route (POC not done)
    const assignedJobs = jobs.filter(
      (j) => !j.collection_completed_at && !j.collection_skipped,
    );

    let nextState: TrackingState;
    if (enRouteJobs.length > 0) {
      nextState = "EN_ROUTE";
    } else if (assignedJobs.length > 0) {
      nextState = "ASSIGNED";
    } else {
      nextState = "IDLE";
    }

    // Flapping guard: ASSIGNED↔EN_ROUTE swaps must wait at least 60s between
    // changes so a brief DB blip (e.g. POC saved/un-saved) doesn't churn the
    // watcher. IDLE always applies immediately — we never want to keep the
    // watcher alive when there is no active job, and we always want to start
    // it the moment a job appears.
    const current = trackingStateRef.current;
    const now = Date.now();
    const isIdleEdge = nextState === "IDLE" || current === "IDLE";
    const withinFlapGuard = now - lastStateChangeRef.current < STATE_FLAP_GUARD_MS;
    // The state actually in force after the flap guard has had its say.
    let effectiveState: TrackingState = current;


    if (nextState !== current && !isIdleEdge && withinFlapGuard) {
      emitTrackingDiag("state_flap_suppressed", { previous_state: current, next_state: nextState });
      console.log(
        `[DriverLocationTracker] Flap guard suppressing ${current} → ${nextState} ` +
        `(${Math.round((STATE_FLAP_GUARD_MS - (now - lastStateChangeRef.current)) / 1000)}s remaining)`
      );
    } else {
      if (nextState !== current) {
        lastStateChangeRef.current = now;
        emitTrackingDiag("state_changed", { previous_state: current, next_state: nextState });
      }
      setTrackingState(nextState);
      effectiveState = nextState;
    }

    console.log('[DriverLocationTracker] Job-state check:', {
      totalActive: jobs.length,
      assigned: assignedJobs.length,
      enRoute: enRouteJobs.length,
      currentState: current,
      nextState,
    });

    emitTrackingDiag("state_evaluated", {
      has_session: true,
      driver_found: true,
      active_jobs: jobs.length,
      assigned_jobs: assignedJobs.length,
      en_route_jobs: enRouteJobs.length,
      previous_state: current,
      next_state: nextState,
      state: effectiveState,
      reason:
        nextState === "EN_ROUTE"
          ? "poc_done_or_skipped_pod_pending"
          : nextState === "ASSIGNED"
          ? "active_job_poc_pending"
          : jobs.length > 0
          ? "active_jobs_outside_en_route_window"
          : "no_active_jobs",
    });

    return { state: effectiveState, isDriverRecord: true, hasSession: true };
  }, []);

  // Keep ref in sync
  checkDriverAndJobsRef.current = checkDriverAndJobs;

  useEffect(() => {
    let jobsChannel: ReturnType<typeof supabase.channel> | null = null;
    let appraisalsChannel: ReturnType<typeof supabase.channel> | null = null;

    const init = async () => {
      await checkDriverAndJobs();

      // Now driverInfoRef is populated — set up scoped realtime
      const driverFilter = driverInfoRef.current?.id
        ? `driver_id=eq.${driverInfoRef.current.id}`
        : undefined;
      const companyFilter = driverInfoRef.current?.companyId
        ? `company_id=eq.${driverInfoRef.current.companyId}`
        : undefined;

      jobsChannel = supabase
        .channel('driver-jobs-tracking')
        .on('postgres_changes', {
          event: '*', schema: 'public', table: 'jobs',
          ...(driverFilter ? { filter: driverFilter } : {}),
        }, () => {
          checkDriverAndJobs();
        })
        .subscribe();

      appraisalsChannel = supabase
        .channel('driver-appraisals-tracking')
        .on('postgres_changes', {
          event: '*', schema: 'public', table: 'vehicle_appraisals',
          ...(companyFilter ? { filter: companyFilter } : {}),
        }, () => {
          checkDriverAndJobs();
        })
        .subscribe();
    };

    emitTrackingDiag("app_launch", { native: isNative });
    init();

    // Diagnostics only: record background/foreground transitions.
    let appStateHandle: { remove: () => void } | null = null;
    let appStateCancelled = false;
    if (isNative) {
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        emitTrackingDiag(isActive ? "app_foreground" : "app_background", {
          state: trackingStateRef.current,
          native: true,
          ms_since_last_callback:
            lastGpsCallbackRef.current > 0 ? Date.now() - lastGpsCallbackRef.current : null,
        });
      })
        .then((h) => {
          appStateHandle = h;
          if (appStateCancelled) h.remove();
        })
        .catch(() => {});
    }

    // Re-check on app resume (handles app restart / return from background).
    // Permissions are NOT primed here — only when tracking starts.
    //
    // Resume must act on the policy as it stands RIGHT NOW. Previously the
    // stale-watcher restart below fired purely because a watcher had been
    // started earlier, so a POD/cancellation/sign-out that landed while the app
    // was backgrounded could be followed by a resurrected watcher. The
    // re-derived snapshot is now fed through the policy first: if tracking is
    // no longer allowed we stop, and we never restart.
    const cleanupResume = onAppResume(async () => {
      console.log('[DriverLocationTracker] App resumed, re-checking jobs');
      invalidatePermissionCache();
      const snapshot = await checkDriverAndJobs();

      const timeSinceLastGps =
        lastGpsCallbackRef.current > 0 ? Date.now() - lastGpsCallbackRef.current : 0;

      const action = resolveResumeAction({
        isMobileDevice: isMobileDeviceRef.current,
        isDriverRecord: snapshot.isDriverRecord,
        trackingState: snapshot.state,
        hasSession: snapshot.hasSession,
        isTracking: hasStartedRef.current,
        watcherStale:
          lastGpsCallbackRef.current > 0 && timeSinceLastGps > STALE_WATCHER_MS,
      });

      if (action === "none") return;

      if (action === "stop") {
        // Policy no longer allows tracking (POD, cancellation, sign-out,
        // no driver record). Tear the native watcher down immediately; the
        // main tracking effect still runs its full stop path off the state
        // change (wake lock, driver is_active=false).
        console.log('[DriverLocationTracker] Resume: tracking no longer allowed — stopping watcher');
        emitTrackingDiag("tracking_stopped", { trigger: "resume_policy_stop", state: snapshot.state });
        if (isNative) {
          nativeCleanupRef.current?.();
          nativeCleanupRef.current = null;
          await stopNativeLocationWatch();
        }
        return;
      }

      // action === "restart": still EN_ROUTE, but the watcher has gone stale.
      if (!isNative) return;
      console.warn(`[DriverLocationTracker] App resumed with stale watcher (${Math.round(timeSinceLastGps / 1000)}s). Restarting...`);
      emitTrackingDiag("watcher_restarted", { trigger: "resume_stale", ms_since_last_callback: timeSinceLastGps });
      callbackSamplerRef.current.watcherStarted();
      nativeCleanupRef.current?.();
      await stopNativeLocationWatch();
      const cleanup = await startNativeLocationWatch(
        (lat, lng, _accuracy) => {
          lastNativePositionRef.current = { lat, lng };
          lastGpsCallbackRef.current = Date.now();
          recoveryRef.current?.onWatcherReading();
          noteCallback();
          updateLocationInDatabase(lat, lng);
        },
        (error) => {
          console.error('[DriverLocationTracker] Native watch error (resumed):', error);
        },
      );
      nativeCleanupRef.current = cleanup;
      lastGpsCallbackRef.current = Date.now();
    });

    // Sign-out must stop tracking immediately. Without this the watcher kept
    // running after logout (no realtime event fires for the signed-out user),
    // leaving location active until the app was force-quit.
    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        emitTrackingDiag("sign_out");
        console.log('[DriverLocationTracker] Signed out — stopping tracking');
        driverInfoRef.current = null;
        setIsDriverRecord(false);
        setTrackingState("IDLE");
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        if (event === "SIGNED_IN") emitTrackingDiag("sign_in");
        checkDriverAndJobsRef.current?.();
      }
    });

    return () => {
      if (jobsChannel) supabase.removeChannel(jobsChannel);
      if (appraisalsChannel) supabase.removeChannel(appraisalsChannel);
      cleanupResume();
      appStateCancelled = true;
      appStateHandle?.remove();
      authSub.subscription.unsubscribe();
    };
  }, [checkDriverAndJobs]);

  // Listen for tracking bridge events (POC/POD lifecycle signals).
  // We always re-derive the state from the DB instead of force-setting it,
  // so transitions stay consistent with the source of truth.
  useEffect(() => {
    const unsub = onTrackingEvent((event: TrackingEvent) => {
      console.log(`[DriverLocationTracker] Bridge: ${event.type}`);
      emitTrackingDiag(event.type === "STOP_TRACKING" ? "pod_signal" : "poc_signal", {
        trigger: String(event.type),
        state: trackingStateRef.current,
      });
      checkDriverAndJobsRef.current?.();
    });
    return unsub;
  }, []);

  // ─── Main tracking start/stop logic ─────────────────────────────────────────
  useEffect(() => {
    const handleTracking = async () => {
      try {
      if (shouldTrack && !hasStartedRef.current) {
        emitTrackingDiag("tracking_requested", {
          state: trackingStateRef.current,
          mode: isNative ? "native" : "web",
          native: isNative,
        });
        // Check permission first (non-blocking)
        if (isNative) {
          // Prime iOS permissions (one-time per session) to resolve "prompt" loop.
          // On iOS, checkPermissions() returns "prompt" until requestPermissions()
          // is called at least once, even if Settings already has "While Using".
          let info = await primeLocationPermissionOnce();

          // If still "prompt" after priming, wait briefly and retry once
          // (iOS may need a moment to update the permission state)
          let retriedAfterPrompt = false;
          if (info.permission === "prompt") {
            retriedAfterPrompt = true;
            await new Promise(r => setTimeout(r, 300));
            invalidatePermissionCache();
            info = await checkLocationPermission(true);
          }

          emitTrackingDiag("permission_result", {
            permission: info.permission,
            precise: info.preciseLocation,
            primed: true,
            retried_after_prompt: retriedAfterPrompt,
          });

          if (info.permission !== "granted") {
            console.log('[DriverLocationTracker] Permission not granted after priming, skipping:', info.permission);
            emitTrackingDiag("tracking_stopped", { trigger: "permission_not_granted", permission: info.permission });
            return;
          }
        }

        hasStartedRef.current = true;
        let success = false;

        if (isNative) {
          // ─── Native: Capacitor Geolocation (works background + locked) ─────
          // SINGLE WATCHER RULE: pick a native distance filter once and keep it
          // for the whole active session. The community BG plugin requires a
          // full restart to change the filter, and every restart is a battery
          // spike. Adaptive tuning happens in JS (AdaptiveTrackingFilter) on
          // top of every callback — no native restart, ever.
          //
          // We pick the TIGHTER baseline (75m) when EN_ROUTE so the JS filter
          // can both pass-through (effective 75m) and loosen (175m) without
          // needing the native layer to deliver more updates than it would
          // have at 75m. For ASSIGNED-only sessions we keep 250m (battery
          // friendly — adaptive tuning is disabled in ASSIGNED).
          const ASSIGNED_FILTER_M = 250;
          const EN_ROUTE_FILTER_M = 75;

          const initialFilter =
            trackingStateRef.current === "EN_ROUTE" ? EN_ROUTE_FILTER_M : ASSIGNED_FILTER_M;

          // Initialise adaptive filter for this session
          adaptiveFilterRef.current = new AdaptiveTrackingFilter();
          adaptiveFilterRef.current.setState(trackingStateRef.current);

          console.log(
            `[DriverLocationTracker] Starting NATIVE tracking — state=${trackingState}, ` +
            `nativeFilter=${initialFilter}m (single-watcher, JS-side adaptive tuning)`
          );

          const handlePosition = (lat: number, lng: number, _accuracy: number, speed?: number | null) => {
            noteCallback();
            lastNativePositionRef.current = { lat, lng };
            lastGpsCallbackRef.current = Date.now();
            recoveryRef.current?.onWatcherReading();

            // JS-side adaptive gate: drop fixes that don't clear the current
            // effective distance filter. NO watcher restart involved.
            const filter = adaptiveFilterRef.current;
            if (filter && !filter.shouldEmit(lat, lng, speed)) {
              return;
            }

            updateLocationInDatabase(lat, lng);
          };

          emitTrackingDiag("watcher_start_requested", {
            state: trackingStateRef.current,
            distance_filter_m: initialFilter,
          });
          callbackSamplerRef.current.watcherStarted();
          const cleanup = await startNativeLocationWatch(
            handlePosition,
            (error) => {
              console.error('[DriverLocationTracker] Native watch error:', error);
            },
            { distanceFilter: initialFilter },
          );
          nativeCleanupRef.current = cleanup;
          lastGpsCallbackRef.current = Date.now();

          // Immediate seed fix: the BG watcher's first callback can take 15-60s on iOS,
          // and short jobs (POC + POD in <60s) would otherwise leave the map stale.
          // Use the foreground Geolocation API to grab a fresh position right now and
          // push it through the same writer (which bypasses throttle for the first fix).
          seedImmediatePositionRef.current?.();

          // Watcher restart (shared by the 5-min backstop and low-power recovery).
          restartWatcherRef.current = async (trigger: string) => {
            const now = Date.now();
            if (!hasStartedRef.current) return;
            staleGuardRef.current.markRestart(now);
            emitTrackingDiag("watcher_restarted", {
              trigger,
              ms_since_last_callback: now - lastGpsCallbackRef.current,
              restart_attempt: staleGuardRef.current.consecutiveRestarts,
            });
            callbackSamplerRef.current.watcherStarted();
            console.warn(`[DriverLocationTracker] Restarting watcher (${trigger}, attempt ${staleGuardRef.current.consecutiveRestarts})`);
            nativeCleanupRef.current?.();
            await stopNativeLocationWatch();
            const restartFilter =
              trackingStateRef.current === "EN_ROUTE" ? EN_ROUTE_FILTER_M : ASSIGNED_FILTER_M;
            const newCleanup = await startNativeLocationWatch(
              handlePosition,
              (error) => console.error('[DriverLocationTracker] Native watch error (recovery):', error),
              { distanceFilter: restartFilter },
            );
            nativeCleanupRef.current = newCleanup;
            lastGpsCallbackRef.current = now;
          };

          // Watcher-health check (no GPS of its own): the existing 5-min
          // backstop restart (StaleWatcherGuard back-off, no loops) plus the
          // one-per-silent-period low-power recovery check.
          staleGuardRef.current.reset();
          recoveryRef.current?.reset();
          heartbeatRef.current = setInterval(async () => {
            const now = Date.now();
            if (!hasStartedRef.current) return;
            if (staleGuardRef.current.shouldRestart(now, lastGpsCallbackRef.current)) {
              await restartWatcherRef.current?.("stale_guard");
              return;
            }
            await recoveryRef.current?.check();
          }, 30_000);

          success = true;
        } else {
          // ─── Web: navigator.geolocation + Wake Lock ────────────────────────
          console.log('[DriverLocationTracker] Starting WEB tracking');
          success = await startWebTracking();
          emitTrackingDiag(success ? "watcher_started" : "watcher_start_failed", { mode: "web" });
        }

        if (success) {
          // Wake lock keeps the SCREEN on — only useful on web where a backgrounded
          // tab stops receiving GPS. On native, the BG plugin keeps GPS alive with
          // the screen off, so forcing the screen on here just burns battery + heat.
          if (!isNative && isWakeLockSupported) {
            await requestWakeLock();
          }

          if (!hasShownStartToastRef.current && !wasTrackingRef.current) {
            hasShownStartToastRef.current = true;
            localStorage.setItem(TOAST_COOLDOWN_KEY, String(Date.now()));
            toast({
              title: "Location Tracking Active",
              description: isNative
                ? "Your location is being tracked. Tracking continues in the background."
                : isWakeLockSupported
                ? "Your location is being shared. Keep this app open for continuous tracking."
                : "Your location is being shared. Please keep this app in the foreground.",
            });
          }
          wasTrackingRef.current = true;
        }
      } else if (!shouldTrack && hasStartedRef.current) {
        console.log('[DriverLocationTracker] Stopping tracking');
        emitTrackingDiag("tracking_stopped", { trigger: "policy_stop", state: trackingStateRef.current });
        hasStartedRef.current = false;
        wasTrackingRef.current = false;

        if (isNative) {
          nativeCleanupRef.current?.();
          nativeCleanupRef.current = null;
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }
          lastNativePositionRef.current = null;
          adaptiveFilterRef.current?.dispose();
          adaptiveFilterRef.current = null;
          // Reset throttle bookkeeping so the next session's first fix writes immediately
          writePolicyRef.current.reset();
          recoveryRef.current?.reset();
          restartWatcherRef.current = null;
          staleGuardRef.current.reset();
          // Drop queued retries so a late retry can't follow the stop.
          writerRef.current?.clear();
          lastSeedAtRef.current = 0;
          await stopNativeLocationWatch();
        } else if (isWebTracking) {
          stopWebTracking(false);
        }

        await releaseWakeLock();

        // Mark driver inactive — retry once on failure to prevent ghost drivers
        const markInactive = async (attempt = 1) => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return;

            const { data: driver } = await supabase
              .from("drivers")
              .select("id")
              .eq("user_id", session.user.id)
              .maybeSingle();

            if (driver) {
              const { error } = await supabase
                .from("drivers")
                .update({ is_active: false })
                .eq("id", driver.id);

              if (error) throw error;
            }
          } catch (err: any) {
            console.error(`[DriverLocationTracker] Failed to mark inactive (attempt ${attempt}):`, err?.message || err);
            if (attempt < 2) {
              // Retry once after 2 seconds
              await new Promise(r => setTimeout(r, 2000));
              return markInactive(attempt + 1);
            }
            // Final failure — log clearly so admin can investigate
            console.error('[DriverLocationTracker] GHOST DRIVER RISK: could not set is_active=false');
          }
        };
        await markInactive();
      }
      } catch (error) {
        console.error('[DriverLocationTracker] handleTracking error:', error);
        emitTrackingDiag("watcher_start_failed", { error_category: categoriseTrackingError(error) });
        hasStartedRef.current = false;
      }
    };

    handleTracking();
  }, [shouldTrack, isNative, isWebTracking, startWebTracking, stopWebTracking, toast, requestWakeLock, releaseWakeLock, isWakeLockSupported, trackingState]);

  // Keep the state ref in sync and propagate ASSIGNED↔EN_ROUTE transitions
  // into the adaptive filter (JS-side tuning only — NEVER restarts the
  // native watcher). IDLE→tracking and tracking→IDLE are handled above.
  // On ASSIGNED→EN_ROUTE we also fire an immediate seed fix so the live map
  // reflects the driver leaving the collection point right after POC.
  const prevTrackingStateRef = useRef<TrackingState>("IDLE");
  useEffect(() => {
    const prev = prevTrackingStateRef.current;
    trackingStateRef.current = trackingState;
    adaptiveFilterRef.current?.setState(trackingState);

    if (
      prev === "ASSIGNED" &&
      trackingState === "EN_ROUTE" &&
      hasStartedRef.current &&
      isNative
    ) {
      console.log("[DriverLocationTracker] POC transition → seeding fresh fix");
      seedImmediatePositionRef.current?.();
    }
    prevTrackingStateRef.current = trackingState;
  }, [trackingState, isNative]);

  // F-08A: the jobs realtime channel is filtered on driver_id = me, so a job
  // reassigned AWAY from this driver (new row has the other driver's id) does
  // not wake it. While tracking, re-evaluate assignments on a slow poll so
  // losing the last active job returns the tracker to IDLE (existing policy).
  useEffect(() => {
    if (trackingState === "IDLE") return;
    const id = setInterval(() => { void checkDriverAndJobs(); }, ASSIGNMENT_RECHECK_MS);
    return () => clearInterval(id);
  }, [trackingState, checkDriverAndJobs]);

  // Hard-disable on desktop
  useEffect(() => {
    const stopIfDesktop = async () => {
      if (!isMobileDevice && (isWebTracking || hasStartedRef.current)) {
        console.log('[DriverLocationTracker] Desktop detected, forcing off');
        emitTrackingDiag("tracking_stopped", { trigger: "desktop_detected" });
        hasStartedRef.current = false;
        nativeCleanupRef.current?.();
        nativeCleanupRef.current = null;
        adaptiveFilterRef.current?.dispose();
        adaptiveFilterRef.current = null;
        await stopNativeLocationWatch();
        await releaseWakeLock();
        await stopWebTracking(true);
      }
    };
    stopIfDesktop();
  }, [isMobileDevice, isWebTracking, releaseWakeLock, stopWebTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hasStartedRef.current) {
        console.log('[DriverLocationTracker] Unmounting, stopping tracking');
        emitTrackingDiag("tracking_stopped", { trigger: "unmount" });
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        writerRef.current?.clear();
        nativeCleanupRef.current?.();
        adaptiveFilterRef.current?.dispose();
        adaptiveFilterRef.current = null;
        stopNativeLocationWatch();
        releaseWakeLock();
        stopWebTracking(true);
      }
    };
  }, [releaseWakeLock, stopWebTracking]);

  return null;
};
